import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Add20Regular,
  ArrowRepeatAll16Regular,
  Building16Regular,
  CalendarLtr16Regular,
  CalendarLtr20Regular,
  Checkmark16Filled,
  Delete20Regular,
  Flag16Filled,
  Person16Regular,
  Search16Regular,
  TextBulletListLtr20Regular,
} from "@fluentui/react-icons";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { SearchableSelect, type SearchableSelectOption } from "../../components/SearchableSelect";
import { useLookupOptions } from "../../lookups/useLookupOptions";
import { useAuth } from "../../auth/AuthContext";
import { apiErrorMessage } from "../../common/apiErrorMessage";
import { addDays, describeDueDate, dueBucket, nextMonday, toIsoDate } from "../../tarefas/taskDates";
import { groupTasks } from "../../tarefas/taskList";
import { TONE_STYLES, URGENCY_OPTIONS, fieldLabel, segmentButton, segmentRadius } from "./tarefasUi";
import { CalendarioTarefas } from "./CalendarioTarefas";
import {
  createTask,
  deleteTask,
  getTaskAssignees,
  getTasks,
  payloadOf,
  updateTask,
  type TaskAssignee,
  type TaskItem,
  type TaskPayload,
  type TaskScope,
  type TaskStatus,
  type TaskUrgency,
} from "../../tarefas/tarefasApi";

const STATUS_OPTIONS: { value: TaskStatus; label: string; style: CSSProperties }[] = [
  { value: "ToDo", label: "Por fazer", style: {} },
  { value: "InProgress", label: "Em curso", style: { backgroundColor: "var(--tt-blue-bg)", color: "var(--tt-blue-fg)" } },
  { value: "Waiting", label: "Em espera", style: { backgroundColor: "var(--tt-purple-bg)", color: "var(--tt-purple-fg)" } },
  { value: "Done", label: "Concluída", style: { backgroundColor: "var(--tt-green-bg)", color: "var(--tt-green-fg)" } },
];

const SCOPE_CHIPS: { code: TaskScope; label: string }[] = [
  { code: "open", label: "Em aberto" },
  { code: "done", label: "Concluídas" },
  { code: "all", label: "Todas" },
];

const statusLabel = (value: string) => STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;
const urgencyLabel = (value: string) => URGENCY_OPTIONS.find((option) => option.value === value)?.label ?? value;

const matchesScope = (task: TaskItem, scope: TaskScope) =>
  scope === "all" || (scope === "done" ? task.status === "Done" : task.status !== "Done");

// ---- Foco -------------------------------------------------------------------------------------
// Muitas ações tiram do ecrã o controlo que tinha o foco (a tarefa muda de grupo, o seletor de
// obra fecha, a linha desaparece). Cada controlo tem `data-control` dentro de `data-task-id`, e
// depois da mudança o foco volta ao mesmo controlo da mesma tarefa — senão caía no início da página.
function focusTaskControl(taskId: number, control: string) {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(`[data-task-id="${taskId}"] [data-control="${control}"]`)
        ?.focus();
    }),
  );
}

function focusVisible(selector: string) {
  requestAnimationFrame(() => {
    const target = Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
      (element) => element.offsetParent !== null,
    );
    target?.focus();
  });
}

const rowHasFocus = (taskId: number) =>
  document.activeElement?.closest("[data-task-id]")?.getAttribute("data-task-id") === String(taskId);

const isInDialog = (target: EventTarget | null) =>
  Boolean((target as HTMLElement | null)?.closest?.('[role="dialog"]')) ||
  Boolean(document.querySelector('[role="dialog"][aria-modal="true"]'));

// ---- Pills ------------------------------------------------------------------------------------
// Cada metadado é um pill do tamanho do seu texto. Os que se escolhem de uma lista têm por cima
// um <select> nativo invisível: abre a lista do sistema (teclado e leitor de ecrã incluídos) sem
// impor a largura da opção mais comprida.

const pillBase: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 8px",
  borderRadius: 999,
  // "font: inherit" primeiro e o tamanho depois: na ordem inversa o shorthand anulava o tamanho.
  font: "inherit",
  fontSize: 12,
  lineHeight: "18px",
  minHeight: 24,
  whiteSpace: "nowrap",
  backgroundColor: "rgba(100,116,139,0.10)",
  color: "var(--foreground)",
  border: "none",
  cursor: "pointer",
};

const mutedPill: CSSProperties = { color: "var(--secondary-text)", backgroundColor: "rgba(100,116,139,0.06)" };

const NAV_KEYS = new Set(["ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"]);

/**
 * Pill com lista de escolha. Com rato grava ao escolher; com teclado as setas só percorrem as
 * opções e grava-se no Enter ou ao sair — em alguns browsers cada seta mudava o valor e gravava,
 * e percorrer a lista passava por "Concluída".
 */
function SelectPill({
  icon,
  labelFor,
  value,
  options,
  ariaLabel,
  style,
  control,
  onChange,
}: {
  icon?: ReactNode;
  labelFor: (value: string) => string;
  value: string;
  options: { value: string; label: string }[];
  ariaLabel: string;
  style?: CSSProperties;
  control?: string;
  onChange: (value: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value);
  const keyboardNav = useRef(false);
  useEffect(() => setDraft(value), [value]);

  const commit = (next: string) => {
    keyboardNav.current = false;
    if (next !== value) onChange(next);
  };

  return (
    <span style={{ ...pillBase, ...style, outline: focused ? "2px solid var(--primary)" : "none", outlineOffset: 1 }}>
      <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {icon}
        {labelFor(draft)}
      </span>
      <select
        value={draft}
        aria-label={ariaLabel}
        data-control={control}
        onKeyDown={(event) => {
          if (NAV_KEYS.has(event.key)) keyboardNav.current = true;
          if (event.key === "Enter") commit(draft);
          if (event.key === "Escape") {
            keyboardNav.current = false;
            setDraft(value);
          }
        }}
        onChange={(event) => {
          setDraft(event.target.value);
          if (!keyboardNav.current) commit(event.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (keyboardNav.current) commit(draft);
        }}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  );
}

/** A data como se diz ("Amanhã", "Há 3 dias"); clicar abre o calendário nativo. */
function DuePill({ task, onChange }: { task: TaskItem; onChange: (iso: string | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Onde o browser não abre o calendário por código, aparece um campo de data visível.
  const [inline, setInline] = useState(false);
  const bucket = task.status === "Done" ? null : dueBucket(task.dueDate);
  const style: CSSProperties =
    bucket === "overdue"
      ? { backgroundColor: "var(--tt-red-bg)", color: "var(--tt-red-fg)", fontWeight: 600 }
      : bucket === "today"
        ? { backgroundColor: "var(--tt-today-bg)", color: "var(--tt-today-fg)", fontWeight: 600 }
        : task.dueDate
          ? {}
          : mutedPill;

  if (inline) {
    return (
      <input
        type="date"
        autoFocus
        data-control="due"
        aria-label={`Data de ${task.title}`}
        defaultValue={task.dueDate ? task.dueDate.slice(0, 10) : ""}
        onChange={(event) => {
          setInline(false);
          onChange(event.target.value || null);
        }}
        onBlur={() => setInline(false)}
        className="px-2 border border-border bg-background text-sm"
        style={{ borderRadius: "var(--radius-button)", height: 26 }}
      />
    );
  }

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        data-control="due"
        aria-label={`Data de ${task.title}: ${describeDueDate(task.dueDate)}. Mudar`}
        onClick={() => {
          const input = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
          try {
            if (input?.showPicker) {
              input.showPicker();
              return;
            }
          } catch {
            // Bloqueado neste contexto: segue para o campo visível.
          }
          setInline(true);
        }}
        style={{ ...pillBase, ...style }}
      >
        <CalendarLtr16Regular aria-hidden="true" />
        {describeDueDate(task.dueDate)}
      </button>
      <input
        ref={inputRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={task.dueDate ? task.dueDate.slice(0, 10) : ""}
        onChange={(event) => onChange(event.target.value || null)}
        style={{ position: "absolute", left: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
    </span>
  );
}

/** Texto editável no sítio: grava ao sair do campo (e no Enter, no título), só se mudou. */
function InlineText({
  value,
  multiline,
  placeholder,
  ariaLabel,
  required,
  muted,
  struck,
  control,
  onSave,
}: {
  value: string;
  multiline?: boolean;
  placeholder?: string;
  ariaLabel: string;
  required?: boolean;
  muted?: boolean;
  struck?: boolean;
  control?: string;
  onSave: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(value), [value]);

  // A nota tem a altura do texto, nem mais (linha vazia por baixo) nem menos (texto cortado).
  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    area.style.height = "auto";
    area.style.height = `${area.scrollHeight}px`;
  }, [draft]);

  const commit = () => {
    const next = draft.trim();
    if (next === value.trim()) return;
    if (required && next === "") {
      setDraft(value);
      return;
    }
    onSave(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      setDraft(value);
      event.currentTarget.blur();
    }
    // Enter grava sem tirar o foco do campo.
    if (event.key === "Enter" && !multiline) commit();
  };

  const style: CSSProperties = {
    borderRadius: "var(--radius-button)",
    font: "inherit",
    fontSize: muted ? "var(--text-xs)" : "var(--text-sm)",
    color: muted ? "var(--secondary-text)" : "var(--foreground)",
    fontWeight: muted ? undefined : 600,
    textDecoration: struck ? "line-through" : undefined,
    resize: "none",
    overflow: "hidden",
  };
  const className = "w-full bg-transparent border border-transparent hover:border-border px-1 py-0.5";

  if (multiline) {
    return (
      <textarea
        ref={areaRef}
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-control={control}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        rows={1}
        className={className}
        style={style}
      />
    );
  }

  return (
    <input
      type="text"
      value={draft}
      placeholder={placeholder}
      aria-label={ariaLabel}
      data-control={control}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className={className}
      style={style}
    />
  );
}

type SaveFn = (taskId: number, changes: Partial<TaskPayload>, control?: string) => void;

function TaskRow({
  task,
  assignees,
  workOptions,
  worksLoading,
  onSave,
  onToggleDone,
  onDelete,
}: {
  task: TaskItem;
  assignees: TaskAssignee[];
  workOptions: SearchableSelectOption<number>[];
  worksLoading: boolean;
  onSave: SaveFn;
  onToggleDone: (task: TaskItem) => void;
  onDelete: (task: TaskItem) => void;
}) {
  // Rato e foco em separado: sair com o rato não pode esconder nada que tenha o foco.
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [editingWork, setEditingWork] = useState(false);
  const active = hovered || focusWithin;
  const done = task.status === "Done";
  const urgency = URGENCY_OPTIONS.find((option) => option.value === task.urgency) ?? URGENCY_OPTIONS[0];
  const status = STATUS_OPTIONS.find((option) => option.value === task.status) ?? STATUS_OPTIONS[0];
  const showNote = Boolean(task.description) || active;

  const responsibleOptions = [
    { value: "", label: "Por atribuir" },
    ...assignees.map((assignee) => ({ value: assignee.userId, label: assignee.name })),
    // Responsável que já não está na lista (conta desativada): mostra-se na mesma.
    ...(task.responsibleUserId && !assignees.some((assignee) => assignee.userId === task.responsibleUserId)
      ? [{ value: task.responsibleUserId, label: task.responsibleName ?? "Utilizador inativo" }]
      : []),
  ];

  const closeWorkEditor = () => {
    setEditingWork(false);
    focusTaskControl(task.id, "work");
  };

  return (
    <li
      data-task-id={task.id}
      className="flex items-start gap-3 px-4 py-3 border-b border-border last:border-b-0"
      style={{ borderLeft: `3px solid ${done ? "transparent" : urgency.accent}` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusWithin(false);
      }}
    >
      {/* Concluir é a ação mais frequente: um clique, sem abrir nada. */}
      <button
        type="button"
        data-control="done"
        aria-label={`Tarefa concluída: ${task.title}`}
        aria-pressed={done}
        onClick={() => onToggleDone(task)}
        className="flex items-center justify-center cursor-pointer"
        style={{
          marginTop: 2,
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: 999,
          border: `2px solid ${done ? "var(--tt-green-fg)" : urgency.value === "Normal" ? "var(--secondary-text)" : urgency.accent}`,
          backgroundColor: done ? "var(--tt-green-fg)" : "transparent",
          color: "#ffffff",
        }}
      >
        {done && <Checkmark16Filled aria-hidden="true" />}
      </button>

      <div className="flex-1" style={{ minWidth: 0, opacity: done ? 0.75 : 1 }}>
        <InlineText
          value={task.title}
          required
          struck={done}
          control="title"
          ariaLabel="Título da tarefa"
          onSave={(title) => onSave(task.id, { title }, "title")}
        />
        {showNote && (
          <InlineText
            value={task.description ?? ""}
            multiline
            muted
            control="note"
            placeholder="Acrescentar nota…"
            ariaLabel={`Nota de ${task.title}`}
            onSave={(description) => onSave(task.id, { description: description || null }, "note")}
          />
        )}

        {/* Um pill por metadado; cada um muda-se clicando nele. */}
        <div className="flex items-center flex-wrap" style={{ gap: 6, marginTop: 6 }}>
          <DuePill task={task} onChange={(dueDate) => onSave(task.id, { dueDate }, "due")} />

          <SelectPill
            labelFor={statusLabel}
            value={task.status}
            control="status"
            ariaLabel={`Estado de ${task.title}`}
            options={STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
            style={task.status === "ToDo" ? mutedPill : { ...status.style, fontWeight: 600 }}
            onChange={(value) => onSave(task.id, { status: value as TaskStatus }, "status")}
          />

          {task.recurringTaskId != null && (
            <Link
              to="/tarefas?vista=calendario"
              title="Gerada pelo calendário: repete-se"
              aria-label={`${task.title}: repete-se, abrir o calendário`}
              style={{ ...pillBase, ...mutedPill, textDecoration: "none" }}
            >
              <ArrowRepeatAll16Regular aria-hidden="true" />
              Repete
            </Link>
          )}

          <SelectPill
            icon={<Flag16Filled aria-hidden="true" />}
            labelFor={urgencyLabel}
            value={task.urgency}
            control="urgency"
            ariaLabel={`Urgência de ${task.title}`}
            options={URGENCY_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
            style={task.urgency === "Normal" ? mutedPill : { ...urgency.style, fontWeight: 600 }}
            onChange={(value) => onSave(task.id, { urgency: value as TaskUrgency }, "urgency")}
          />

          <SelectPill
            icon={<Person16Regular aria-hidden="true" />}
            labelFor={(value) => responsibleOptions.find((option) => option.value === value)?.label ?? "Por atribuir"}
            value={task.responsibleUserId ?? ""}
            control="responsible"
            ariaLabel={`Responsável por ${task.title}`}
            options={responsibleOptions}
            style={task.responsibleUserId ? undefined : mutedPill}
            onChange={(value) => onSave(task.id, { responsibleUserId: value || null }, "responsible")}
          />

          {editingWork ? (
            <div
              style={{ width: 260 }}
              onKeyDown={(event) => {
                if (event.key === "Escape") closeWorkEditor();
              }}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setEditingWork(false);
              }}
            >
              <SearchableSelect<number>
                value={task.workId}
                options={workOptions}
                loading={worksLoading}
                allowClear
                autoFocus
                panelPosition="fixed"
                placeholder="Escolher obra"
                searchPlaceholder="Código ou nome da obra…"
                emptyMessage="Nenhuma obra bate com o texto."
                ariaLabel={`Obra de ${task.title}`}
                onChange={(workId) => {
                  setEditingWork(false);
                  onSave(task.id, { workId }, "work");
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              data-control="work"
              aria-label={`Obra de ${task.title}: ${task.workName ?? "sem obra"}. Mudar`}
              onClick={() => setEditingWork(true)}
              style={{ ...pillBase, ...(task.workName ? {} : mutedPill), maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              <Building16Regular aria-hidden="true" />
              {task.workName ?? "Sem obra"}
            </button>
          )}
        </div>
      </div>

      {/* Apagar à vista com o rato por cima ou o foco na linha. Transparente (e não escondido)
          fora disso, para continuar na ordem do Tab e para os leitores de ecrã. */}
      <button
        type="button"
        data-control="delete"
        aria-label={`Apagar ${task.title}`}
        onClick={() => onDelete(task)}
        className="p-1 text-secondary-text hover:bg-accent/30 cursor-pointer"
        style={{ borderRadius: 6, opacity: active ? 1 : 0, flexShrink: 0 }}
      >
        <Delete20Regular aria-hidden="true" />
      </button>
    </li>
  );
}

/**
 * Criar uma tarefa: os campos todos à vista, com os valores que poupam cliques já postos
 * (por fazer, normal, a cargo de quem cria). Enter no título cria e deixa o formulário pronto
 * para a seguinte; a data escolhe-se com um clique nos atalhos.
 */
function TaskComposer({
  assignees,
  currentUserId,
  workOptions,
  worksLoading,
  focusTick,
  onCreate,
  onClose,
}: {
  assignees: TaskAssignee[];
  currentUserId: string | null;
  workOptions: SearchableSelectOption<number>[];
  worksLoading: boolean;
  /** Muda quando o atalho é usado com o formulário já aberto: o foco volta ao título. */
  focusTick: number;
  onCreate: (payload: Partial<TaskPayload> & { title: string }) => Promise<boolean>;
  onClose: () => void;
}) {
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [urgency, setUrgency] = useState<TaskUrgency>("Normal");
  const [responsibleUserId, setResponsibleUserId] = useState<string>(currentUserId ?? "");
  const [workId, setWorkId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    titleRef.current?.focus();
    // No Mac, Option+N dentro de um campo de texto é uma tecla "morta" (ñ) e a composição devolve
    // o foco ao campo de origem logo a seguir: insiste-se no frame seguinte.
    const frame = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [focusTick]);

  const today = new Date();
  const quickDates: { label: string; iso: string }[] = [
    { label: "Hoje", iso: toIsoDate(today) },
    { label: "Amanhã", iso: toIsoDate(addDays(today, 1)) },
    { label: "Próxima semana", iso: toIsoDate(nextMonday(today)) },
  ];

  const submit = async () => {
    if (title.trim() === "" || isSaving) return;
    setIsSaving(true);
    const created = await onCreate({
      title: title.trim(),
      description: description.trim() || null,
      dueDate,
      urgency,
      responsibleUserId: responsibleUserId || null,
      workId,
    });
    setIsSaving(false);
    if (created) {
      setTitle("");
      setDescription("");
      setDueDate(null);
      setUrgency("Normal");
      setResponsibleUserId(currentUserId ?? "");
      setWorkId(null);
      titleRef.current?.focus();
    }
  };

  return (
    <form
      className="bg-card border border-border p-4 space-y-3"
      style={{ borderRadius: "var(--radius)" }}
      aria-label="Nova tarefa"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <input
        ref={titleRef}
        type="text"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="O que há para fazer?"
        aria-label="Título da nova tarefa"
        className="w-full px-3 py-2 border border-border bg-background text-foreground"
        style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-base, 16px)", fontWeight: 600 }}
      />
      <textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Nota (opcional)"
        aria-label="Nota da nova tarefa"
        rows={2}
        className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm"
        style={{ borderRadius: "var(--radius-button)", resize: "vertical", font: "inherit" }}
      />

      <div className="flex flex-wrap" style={{ gap: 20, alignItems: "flex-end" }}>
        <div role="group" aria-label="Data">
          <div style={fieldLabel} aria-hidden="true">Data</div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <div className="flex">
              {quickDates.map((option, index) => {
                const selected = dueDate === option.iso;
                return (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setDueDate(selected ? null : option.iso)}
                    style={{
                      ...segmentButton(selected),
                      marginLeft: index === 0 ? 0 : -1,
                      borderRadius: segmentRadius(index, quickDates.length),
                    }}
                  >
                    {selected && <Checkmark16Filled aria-hidden="true" />}
                    {option.label}
                  </button>
                );
              })}
            </div>
            <input
              type="date"
              value={dueDate ?? ""}
              aria-label="Outra data"
              onChange={(event) => setDueDate(event.target.value || null)}
              className="px-2 py-1 border border-border bg-background text-sm"
              style={{ borderRadius: "var(--radius-button)", height: 34, color: "var(--foreground)" }}
            />
          </div>
        </div>

        <div role="group" aria-label="Urgência">
          <div style={fieldLabel} aria-hidden="true">Urgência</div>
          <div className="flex">
            {URGENCY_OPTIONS.map((option, index) => {
              const selected = urgency === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setUrgency(option.value)}
                  style={{
                    ...segmentButton(selected, option.value === "Normal" ? undefined : option.style),
                    marginLeft: index === 0 ? 0 : -1,
                    borderRadius: segmentRadius(index, URGENCY_OPTIONS.length),
                  }}
                >
                  {selected && <Checkmark16Filled aria-hidden="true" />}
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <label style={{ display: "block" }}>
          <div style={fieldLabel}>Responsável</div>
          <select
            value={responsibleUserId}
            onChange={(event) => setResponsibleUserId(event.target.value)}
            className="px-2 border border-border bg-background text-foreground text-sm"
            style={{ borderRadius: "var(--radius-button)", height: 34, minWidth: 180 }}
          >
            <option value="">Por atribuir</option>
            {assignees.map((assignee) => (
              <option key={assignee.userId} value={assignee.userId}>
                {assignee.userId === currentUserId ? `${assignee.name} (eu)` : assignee.name}
              </option>
            ))}
          </select>
        </label>

        <div style={{ width: 260 }}>
          <div style={fieldLabel} aria-hidden="true">Obra (opcional)</div>
          <SearchableSelect<number>
            value={workId}
            options={workOptions}
            loading={worksLoading}
            allowClear
            panelPosition="fixed"
            placeholder="Sem obra"
            searchPlaceholder="Código ou nome da obra…"
            emptyMessage="Nenhuma obra bate com o texto."
            ariaLabel="Obra da nova tarefa"
            onChange={(value) => setWorkId(value)}
          />
        </div>
      </div>

      <div className="flex items-center flex-wrap" style={{ gap: 8, justifyContent: "space-between" }}>
        <span className="text-xs text-secondary-text">
          Enter no título cria a tarefa e deixa o formulário pronto para a seguinte · Esc fecha
        </span>
        <div className="flex" style={{ gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 cursor-pointer"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Fechar
          </button>
          <button
            type="submit"
            disabled={isSaving || title.trim() === ""}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground cursor-pointer disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {isSaving ? "A criar…" : "Criar tarefa"}
          </button>
        </div>
      </div>
    </form>
  );
}

type UrgencyFilter = "" | "urgent-plus" | "VeryUrgent";

// Todos os controlos da barra de filtros com a mesma altura e o mesmo arredondamento do seletor
// de obra (--button-height, --radius-button), para a barra ler-se como uma peça só.
const filterControl: CSSProperties = {
  height: "var(--button-height)",
  borderRadius: "var(--radius-button)",
  border: "1px solid var(--border)",
  fontSize: "var(--text-sm)",
};

const filterPill = (active: boolean): CSSProperties => ({
  ...filterControl,
  padding: "0 12px",
  gap: 6,
  backgroundColor: active ? "rgba(234,88,12,0.10)" : "var(--background)",
  color: active ? "var(--tt-accent-text)" : "var(--foreground)",
  fontWeight: active ? 600 : 400,
});

interface Toast {
  id: number;
  text: string;
  undo?: () => Promise<void> | void;
  /** Depois de apagar, o foco vai para o Desfazer: a linha que o tinha já não existe. */
  focusUndo?: boolean;
}

const TOAST_MS = 10_000;

export function TarefasView() {
  const { user } = useAuth();
  const currentUserId = user?.userId || null;

  const [tasks, setTasks] = useState<TaskItem[]>([]);
  // Espelho síncrono da lista: as gravações em fila partem sempre do estado mais recente.
  const tasksRef = useRef<TaskItem[]>([]);
  const setTaskList = useCallback((update: (previous: TaskItem[]) => TaskItem[]) => {
    setTasks((previous) => {
      const next = update(previous);
      tasksRef.current = next;
      return next;
    });
  }, []);

  const [assignees, setAssignees] = useState<TaskAssignee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [toast, setToast] = useState<Toast | null>(null);
  const [toastPaused, setToastPaused] = useState(false);
  const toastRef = useRef<HTMLDivElement>(null);
  const undoRef = useRef<HTMLButtonElement>(null);
  const toastSeq = useRef(0);

  const [scope, setScope] = useState<TaskScope>("open");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  // Filtros finos: a lista é curta, por isso filtram no browser e respondem sem esperar.
  const [responsibleFilter, setResponsibleFilter] = useState(""); // "" todos · "me" · "none" · userId
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyFilter>("");
  const [statusFilter, setStatusFilter] = useState<"" | TaskStatus>("");
  const [workFilter, setWorkFilter] = useState<number | null>(null);

  const [composerOpen, setComposerOpen] = useState(false);
  const [composerFocusTick, setComposerFocusTick] = useState(0);

  // Uma tarefa concluída agora mesmo fica no sítio (riscada) até se trocar de filtro.
  const [keepInPlace, setKeepInPlace] = useState<ReadonlySet<number>>(() => new Set());
  // O estado que a tarefa tinha antes de ser concluída, para reabrir no mesmo (e não em "Por fazer").
  const statusBeforeDone = useRef(new Map<number, TaskStatus>());

  const saveQueues = useRef(new Map<number, Promise<unknown>>());
  const pendingSaves = useRef(new Map<number, number>());
  const loadSeq = useRef(0);

  const { options: workOptions, loading: worksLoading } = useLookupOptions("works");

  const announce = () => window.dispatchEvent(new Event("aja2:tasks"));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const showToast = useCallback((next: Omit<Toast, "id">) => {
    toastSeq.current += 1;
    setToastPaused(false);
    setToast({ ...next, id: toastSeq.current });
  }, []);

  const dismissToast = useCallback(() => {
    const hadFocus = toastRef.current?.contains(document.activeElement) ?? false;
    setToast(null);
    setToastPaused(false);
    // O aviso tinha o foco e vai desaparecer: o foco passa para a lista, não para o início da página.
    if (hadFocus) focusVisible("[data-tasks-list]");
  }, []);

  // O aviso some sozinho, mas não enquanto o rato ou o foco estão nele — o Desfazer tem de ser
  // alcançável com calma, também ao teclado.
  useEffect(() => {
    if (!toast || toastPaused) return;
    const timer = setTimeout(dismissToast, TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast, toastPaused, dismissToast]);

  useEffect(() => {
    if (toast?.focusUndo) undoRef.current?.focus();
  }, [toast]);

  const load = useCallback(async () => {
    const sequence = ++loadSeq.current;
    setError(null);
    try {
      const result = await getTasks({ scope, search: debouncedSearch });
      if (sequence !== loadSeq.current) return;
      // Uma tarefa com gravações a meio fica como está no ecrã: a resposta da lista é mais antiga.
      setTaskList(() =>
        result.map((task) =>
          (pendingSaves.current.get(task.id) ?? 0) > 0
            ? tasksRef.current.find((current) => current.id === task.id) ?? task
            : task,
        ),
      );
    } catch (err) {
      if (sequence !== loadSeq.current) return;
      setError(apiErrorMessage(err, "Não foi possível carregar as tarefas."));
    } finally {
      if (sequence === loadSeq.current) setIsLoading(false);
    }
  }, [scope, debouncedSearch, setTaskList]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    getTaskAssignees()
      .then(setAssignees)
      .catch(() => setAssignees([]));
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const view: "list" | "calendar" = searchParams.get("vista") === "calendario" ? "calendar" : "list";
  const [newMacroTick, setNewMacroTick] = useState(0);
  // Ao trocar de vista a lista (ou o calendário) desaparece com o foco lá dentro: o foco vai para
  // o título do calendário, ou para o botão de criar na lista. Não na primeira abertura da página.
  const previousView = useRef(view);
  useEffect(() => {
    if (previousView.current === view) return;
    previousView.current = view;
    focusVisible(view === "calendar" ? "[data-calendar-title]" : "[data-new-task]");
  }, [view]);
  const changeView = (next: "list" | "calendar") => {
    if (next === view) return;
    setSearchParams(next === "calendar" ? { vista: "calendario" } : {}, { replace: true });
    // As tarefas geradas pelo calendário entretanto aparecem na lista sem recarregar a página.
    if (next === "list") void load();
  };

  const openComposer = useCallback(() => {
    setComposerOpen(true);
    setComposerFocusTick((tick) => tick + 1);
  }, []);

  const closeComposer = () => {
    setComposerOpen(false);
    focusVisible("[data-new-task]");
  };

  // Alt+N abre o formulário de qualquer sítio do ecrã. Com modificador, para não disparar ao
  // escrever nem por comandos de voz com letras soltas.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.code !== "KeyN" || event.repeat) return;
      if (isInDialog(event.target)) return;
      event.preventDefault();
      if (view === "calendar") {
        setNewMacroTick((tick) => tick + 1);
      } else {
        openComposer();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openComposer, view]);

  const changeScope = (next: TaskScope) => {
    if (next === scope) return;
    setScope(next);
    // A lista antiga não pode aparecer debaixo do título novo enquanto a nova chega.
    setTaskList(() => []);
    setIsLoading(true);
    setKeepInPlace(new Set());
    if (next === "done") setStatusFilter("");
  };

  const hasFilters = Boolean(responsibleFilter || urgencyFilter || statusFilter || workFilter != null || search);
  const clearFilters = () => {
    setResponsibleFilter("");
    setUrgencyFilter("");
    setStatusFilter("");
    setWorkFilter(null);
    setSearch("");
  };

  /** Os nomes que acompanham os ids, para o pill mostrar logo o novo valor. */
  const derivedFields = (changes: Partial<TaskPayload>, before: TaskItem): Partial<TaskItem> => {
    const derived: Partial<TaskItem> = {};
    if ("responsibleUserId" in changes) {
      derived.responsibleName = changes.responsibleUserId
        ? assignees.find((assignee) => assignee.userId === changes.responsibleUserId)?.name ?? before.responsibleName
        : null;
    }
    if ("workId" in changes) {
      const option = workOptions.find((item) => item.value === changes.workId);
      derived.workName = changes.workId != null ? (option ? String(option.label) : before.workName) : null;
    }
    if ("status" in changes) {
      derived.completedAt = changes.status === "Done" ? before.completedAt ?? new Date().toISOString() : null;
    }
    return derived;
  };

  /**
   * Grava uma alteração de uma tarefa. Muda logo no ecrã; as gravações da mesma tarefa vão em
   * fila, cada uma só com os seus campos; se uma falhar, desfaz-se só o que ela mudou.
   */
  const save = useCallback(
    (taskId: number, changes: Partial<TaskPayload>, control?: string): Promise<TaskItem | null> => {
      const before = tasksRef.current.find((task) => task.id === taskId);
      if (!before) return Promise.resolve(null);

      const patch: Partial<TaskItem> = { ...changes, ...derivedFields(changes, before) };
      const patchKeys = Object.keys(patch) as (keyof TaskItem)[];
      const previous = Object.fromEntries(patchKeys.map((key) => [key, before[key]])) as Partial<TaskItem>;
      const keepFocus = control !== undefined && rowHasFocus(taskId);

      setError(null);
      setTaskList((list) => list.map((task) => (task.id === taskId ? { ...task, ...patch } : task)));
      if (keepFocus && control) focusTaskControl(taskId, control);

      // Concluir, venha do círculo ou do pill de estado, segue sempre o mesmo caminho.
      if (changes.status === "Done" && before.status !== "Done") {
        statusBeforeDone.current.set(taskId, before.status);
        setKeepInPlace((ids) => new Set(ids).add(taskId));
        showToast({
          text: `"${before.title}" concluída.`,
          undo: () => {
            void save(taskId, { status: before.status }, "done");
          },
        });
      }

      pendingSaves.current.set(taskId, (pendingSaves.current.get(taskId) ?? 0) + 1);
      const run = (saveQueues.current.get(taskId) ?? Promise.resolve()).then(async () => {
        try {
          const saved = await updateTask(taskId, changes);
          const remaining = (pendingSaves.current.get(taskId) ?? 1) - 1;
          pendingSaves.current.set(taskId, remaining);
          setTaskList((list) =>
            list.map((task) => {
              if (task.id !== taskId) return task;
              // Com outras alterações ainda em fila, só se aceitam do servidor os campos desta.
              if (remaining > 0) {
                const accepted = Object.fromEntries(patchKeys.map((key) => [key, saved[key]]));
                return { ...task, ...accepted };
              }
              return saved;
            }),
          );
          announce();
          return saved;
        } catch (err) {
          pendingSaves.current.set(taskId, (pendingSaves.current.get(taskId) ?? 1) - 1);
          setTaskList((list) =>
            list.map((task) => {
              if (task.id !== taskId) return task;
              const reverted: Record<string, unknown> = { ...task };
              for (const key of patchKeys) {
                // Só se repõe o campo que ainda tem o valor desta alteração.
                if (task[key] === patch[key]) reverted[key] = previous[key];
              }
              return reverted as unknown as TaskItem;
            }),
          );
          setError(apiErrorMessage(err, "Não foi possível gravar a tarefa."));
          return null;
        }
      });
      saveQueues.current.set(taskId, run);
      return run;
    },
    // derivedFields lê assignees e workOptions; o resto são refs e setters estáveis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignees, workOptions, setTaskList, showToast],
  );

  const toggleDone = (task: TaskItem) => {
    if (task.status === "Done") {
      void save(task.id, { status: statusBeforeDone.current.get(task.id) ?? "ToDo" }, "done");
    } else {
      void save(task.id, { status: "Done" }, "done");
    }
  };

  const handleCreate = async (payload: Partial<TaskPayload> & { title: string }): Promise<boolean> => {
    setError(null);
    try {
      const created = await createTask(payload);
      // Uma lista ainda a caminho já não traria esta tarefa: deixa de contar.
      loadSeq.current += 1;
      setIsLoading(false);
      if (matchesScope(created, scope)) setTaskList((list) => [created, ...list]);
      showToast({
        text: `"${created.title}" criada.`,
        undo: async () => {
          try {
            await deleteTask(created.id);
            setTaskList((list) => list.filter((task) => task.id !== created.id));
            announce();
          } catch (err) {
            setError(apiErrorMessage(err, "Não foi possível desfazer a criação."));
          }
        },
      });
      announce();
      return true;
    } catch (err) {
      setError(apiErrorMessage(err, "Não foi possível criar a tarefa."));
      return false;
    }
  };

  // Apagar não pergunta: o Desfazer do aviso é a rede, e recebe o foco para estar a um Enter.
  const handleDelete = async (task: TaskItem) => {
    setError(null);
    try {
      await deleteTask(task.id);
      setTaskList((list) => list.filter((item) => item.id !== task.id));
      announce();
      showToast({
        text: `"${task.title}" apagada.`,
        focusUndo: true,
        undo: async () => {
          try {
            const restored = await createTask(payloadOf(task));
            if (matchesScope(restored, scope)) {
              setTaskList((list) => [restored, ...list]);
              focusTaskControl(restored.id, "title");
            }
            announce();
          } catch (err) {
            setError(apiErrorMessage(err, "Não foi possível repor a tarefa."));
          }
        },
      });
    } catch (err) {
      setError(apiErrorMessage(err, "Não foi possível apagar a tarefa."));
    }
  };

  const shown = useMemo(
    () =>
      tasks.filter((task) => {
        if (scope === "open" && task.status === "Done" && !keepInPlace.has(task.id)) return false;
        if (responsibleFilter === "me" && task.responsibleUserId !== currentUserId) return false;
        if (responsibleFilter === "none" && task.responsibleUserId) return false;
        if (responsibleFilter && responsibleFilter !== "me" && responsibleFilter !== "none"
          && task.responsibleUserId !== responsibleFilter) return false;
        if (urgencyFilter === "urgent-plus" && task.urgency === "Normal") return false;
        if (urgencyFilter === "VeryUrgent" && task.urgency !== "VeryUrgent") return false;
        if (statusFilter && task.status !== statusFilter && !keepInPlace.has(task.id)) return false;
        if (workFilter != null && task.workId !== workFilter) return false;
        return true;
      }),
    [tasks, scope, keepInPlace, responsibleFilter, urgencyFilter, statusFilter, workFilter, currentUserId],
  );

  const groups = useMemo(() => groupTasks(shown, scope, keepInPlace), [shown, scope, keepInPlace]);
  const openCount = groups.reduce((total, group) => total + (group.key === "done" ? group.items.length : group.openCount), 0);

  const responsibleFilterLabel = (value: string) =>
    value === ""
      ? "Responsável: Todos"
      : value === "me"
        ? "Responsável: Eu"
        : value === "none"
          ? "Responsável: Por atribuir"
          : `Responsável: ${assignees.find((assignee) => assignee.userId === value)?.name ?? "—"}`;

  return (
    <div className="tarefas-root flex flex-col h-full">
      <style>{TONE_STYLES}</style>
      <PageHeader
        title="Tarefas"
        subtitle="O que há para fazer, agrupado por quando. Concluir é um clique; tudo o resto edita-se no sítio."
        actions={
          <div className="flex items-center" style={{ gap: 8 }}>
            <div className="flex" role="group" aria-label="Vista">
              {(
                [
                  { key: "list", label: "Lista", icon: <TextBulletListLtr20Regular aria-hidden="true" /> },
                  { key: "calendar", label: "Calendário", icon: <CalendarLtr20Regular aria-hidden="true" /> },
                ] as const
              ).map((option, index) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={view === option.key}
                  onClick={() => changeView(option.key)}
                  style={{
                    ...segmentButton(view === option.key),
                    marginLeft: index === 0 ? 0 : -1,
                    borderRadius: segmentRadius(index, 2),
                    height: "var(--button-height)",
                  }}
                >
                  {option.icon}
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-new-task
              onClick={view === "calendar" ? () => setNewMacroTick((tick) => tick + 1) : openComposer}
              aria-keyshortcuts="Alt+N"
              className="px-4 py-2 bg-primary text-primary-foreground flex items-center gap-2 cursor-pointer"
              style={{ borderRadius: "var(--radius-button)", height: "var(--button-height)" }}
            >
              <Add20Regular aria-hidden="true" />
              {view === "calendar" ? "Nova tarefa que se repete" : "Nova tarefa"}
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {view === "calendar" ? (
          <CalendarioTarefas
            assignees={assignees}
            currentUserId={currentUserId}
            workOptions={workOptions}
            worksLoading={worksLoading}
            newMacroTick={newMacroTick}
            onChanged={announce}
          />
        ) : (
          <>
          {composerOpen && (
            <TaskComposer
              assignees={assignees}
              currentUserId={currentUserId}
              workOptions={workOptions}
              worksLoading={worksLoading}
              focusTick={composerFocusTick}
              onCreate={handleCreate}
              onClose={closeComposer}
            />
          )}

          {/* Uma só barra, toda à esquerda: pesquisa, o que mostrar e os filtros finos. */}
          <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
            <label
              className="flex items-center bg-background"
              style={{
                ...filterControl,
                padding: "0 10px",
                gap: 6,
                width: 260,
                outline: searchFocused ? "2px solid var(--primary)" : "none",
                outlineOffset: 1,
              }}
            >
              <Search16Regular aria-hidden="true" style={{ color: "var(--secondary-text)", flexShrink: 0 }} />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Procurar tarefa, nota ou obra…"
                aria-label="Procurar tarefas"
                className="flex-1 bg-transparent text-foreground"
                style={{ border: "none", outline: "none", font: "inherit", fontSize: "var(--text-sm)", minWidth: 0 }}
              />
            </label>

            <div className="flex" role="group" aria-label="Mostrar">
              {SCOPE_CHIPS.map((chip, index) => {
                const selected = scope === chip.code;
                const first = index === 0;
                const last = index === SCOPE_CHIPS.length - 1;
                return (
                  <button
                    key={chip.code}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => changeScope(chip.code)}
                    className="cursor-pointer"
                    style={{
                      ...filterControl,
                      padding: "0 12px",
                      marginLeft: first ? 0 : -1,
                      borderRadius: first
                        ? "var(--radius-button) 0 0 var(--radius-button)"
                        : last
                          ? "0 var(--radius-button) var(--radius-button) 0"
                          : 0,
                      background: selected ? "var(--primary)" : "var(--background)",
                      color: selected ? "var(--primary-foreground)" : "var(--foreground)",
                      borderColor: selected ? "var(--primary)" : "var(--border)",
                      fontWeight: selected ? 600 : 400,
                      position: "relative",
                      zIndex: selected ? 1 : 0,
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>

            <SelectPill
              icon={<Person16Regular aria-hidden="true" />}
              labelFor={responsibleFilterLabel}
              value={responsibleFilter}
              ariaLabel="Responsável"
              options={[
                { value: "", label: "Todos" },
                { value: "me", label: "Eu" },
                { value: "none", label: "Por atribuir" },
                ...assignees
                  .filter((assignee) => assignee.userId !== currentUserId)
                  .map((assignee) => ({ value: assignee.userId, label: assignee.name })),
              ]}
              style={filterPill(responsibleFilter !== "")}
              onChange={setResponsibleFilter}
            />
            <SelectPill
              icon={<Flag16Filled aria-hidden="true" />}
              labelFor={(value) =>
                `Urgência: ${value === "" ? "Todas" : value === "urgent-plus" ? "Urgentes" : "Muito urgentes"}`
              }
              value={urgencyFilter}
              ariaLabel="Urgência"
              options={[
                { value: "", label: "Todas" },
                { value: "urgent-plus", label: "Urgentes e muito urgentes" },
                { value: "VeryUrgent", label: "Só muito urgentes" },
              ]}
              style={filterPill(urgencyFilter !== "")}
              onChange={(value) => setUrgencyFilter(value as UrgencyFilter)}
            />
            {scope !== "done" && (
              <SelectPill
                labelFor={(value) => `Estado: ${value === "" ? "Todos" : statusLabel(value)}`}
                value={statusFilter}
                ariaLabel="Estado"
                options={[
                  { value: "", label: "Todos" },
                  ...STATUS_OPTIONS.filter((option) => option.value !== "Done").map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
                ]}
                style={filterPill(statusFilter !== "")}
                onChange={(value) => setStatusFilter(value as "" | TaskStatus)}
              />
            )}
            <div style={{ width: 220 }}>
              <SearchableSelect<number>
                value={workFilter}
                options={workOptions}
                loading={worksLoading}
                allowClear
                panelPosition="fixed"
                placeholder="Obra: todas"
                searchPlaceholder="Código ou nome da obra…"
                emptyMessage="Nenhuma obra bate com o texto."
                ariaLabel="Obra"
                onChange={(value) => setWorkFilter(value)}
              />
            </div>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm cursor-pointer"
                style={{ background: "transparent", border: "none", color: "var(--tt-accent-text)", textDecoration: "underline" }}
              >
                Limpar filtros
              </button>
            )}
            <span className="text-sm text-secondary-text" role="status" style={{ marginLeft: 4 }}>
              {isLoading ? "" : `${openCount} tarefa(s)`}
            </span>
          </div>

          {scope !== "open" && (
            <p className="text-xs text-secondary-text">
              As concluídas mostram-se durante 90 dias depois de concluídas.
            </p>
          )}

          {error && (
            <div
              role="alert"
              className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}

          <div data-tasks-list tabIndex={-1} aria-label="Lista de tarefas" style={{ outline: "none" }} className="space-y-4">
            {isLoading ? (
              <p className="text-sm text-secondary-text" role="status">A carregar tarefas…</p>
            ) : groups.length === 0 ? (
              <div className="bg-card border border-border p-6 text-sm text-secondary-text" style={{ borderRadius: "var(--radius)" }}>
                {hasFilters
                  ? "Nenhuma tarefa com estes filtros."
                  : scope === "done"
                    ? "Ainda não há tarefas concluídas."
                    : "Nada por fazer. Carregue em Nova tarefa (ou Alt+N) para acrescentar."}
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.key}>
                  <h2
                    id={`tarefas-grupo-${group.key}`}
                    className="text-sm mb-2"
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)" as CSSProperties["fontWeight"],
                      color: group.key === "overdue" ? "var(--tt-red-fg)" : "var(--foreground)",
                    }}
                  >
                    {group.label}{" "}
                    <span className="text-secondary-text">
                      · {group.key === "done" ? group.items.length : group.openCount}
                    </span>
                  </h2>
                  <ul
                    aria-labelledby={`tarefas-grupo-${group.key}`}
                    className="bg-card border border-border"
                    style={{ borderRadius: "var(--radius)", listStyle: "none", margin: 0, padding: 0 }}
                  >
                    {group.items.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        assignees={assignees}
                        workOptions={workOptions}
                        worksLoading={worksLoading}
                        onSave={(taskId, changes, control) => void save(taskId, changes, control)}
                        onToggleDone={toggleDone}
                        onDelete={(item) => void handleDelete(item)}
                      />
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
          </>
        )}
      </div>

      {/* Aviso flutuante: sempre montado para os leitores de ecrã anunciarem; não empurra nada. */}
      <div
        aria-live="polite"
        style={{ position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)", zIndex: 60 }}
      >
        {toast && (
          <div
            ref={toastRef}
            className="flex items-center gap-3 px-4 py-2 text-sm shadow-lg"
            style={{ borderRadius: "var(--radius)", backgroundColor: "#1f2937", color: "#ffffff" }}
            onMouseEnter={() => setToastPaused(true)}
            onMouseLeave={() => setToastPaused(toastRef.current?.contains(document.activeElement) ?? false)}
            onFocus={() => setToastPaused(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setToastPaused(false);
            }}
          >
            <span>{toast.text}</span>
            {toast.undo && (
              <button
                ref={undoRef}
                type="button"
                onClick={() => {
                  const undo = toast.undo;
                  setToast(null);
                  setToastPaused(false);
                  void undo?.();
                }}
                className="cursor-pointer"
                style={{ color: "#fdba74", fontWeight: 600, background: "transparent", border: "none" }}
              >
                Desfazer
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
